import { useFrame, useThree } from "@react-three/fiber";
import {
  createContext,
  type ReactNode,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import type { Group } from "three";
import { HardwareInstanceManager } from "./hardwareInstancing";
import { subscribeToSceneChanges } from "./sceneActivity";

const HardwareInstancingContext = createContext<HardwareInstanceManager | null>(
  null,
);

export const useHardwareInstancing = () =>
  useContext(HardwareInstancingContext);

/**
 * Place directly inside each InteractiveGroup, around that group's parts.
 * Product bounds collectors must skip userData.isHardwareBatch: the logical
 * meshes already retain the tighter per-part bounds, without rotated batch AABBs.
 */
export function HardwareInstancingProvider({
  animationGroup,
  enabled = true,
  children,
}: Readonly<{
  animationGroup: string;
  enabled?: boolean;
  children: ReactNode;
}>) {
  const scene = useThree((state) => state.scene);
  const invalidate = useThree((state) => state.invalidate);
  const root = useRef<Group>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a replacement scene needs fresh registrations, not a disposed manager.
  const manager = useMemo(
    () => new HardwareInstanceManager(animationGroup, invalidate),
    [animationGroup, invalidate, scene],
  );

  useLayoutEffect(() => {
    if (!root.current) return;
    manager.attach(root.current);
    const unsubscribe = subscribeToSceneChanges(scene, () =>
      manager.markDirty(),
    );
    return () => {
      unsubscribe();
      manager.dispose();
    };
  }, [manager, scene]);

  // No dependency array on purpose: a re-render may have moved a registered
  // mesh, and there is no cheaper signal for that than "something rendered".
  // It only flags the batches, though — asking for a frame here as well would
  // wake the demand-rendered canvas on every React render, including the many
  // that change nothing this manager can see.
  useLayoutEffect(() => manager.markStale());
  useFrame(() => {
    manager.flush();
  }, -1);

  return (
    <HardwareInstancingContext.Provider value={enabled ? manager : null}>
      <group ref={root}>{children}</group>
    </HardwareInstancingContext.Provider>
  );
}
